import { useSyncExternalStore } from 'react';
export type Theme = 'light' | 'dark';
export type ThemePreference = Theme | 'system';
export const THEME_KEY = 'pp_theme';
export function validPreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}
export function createThemeStore(browser: Window) {
  const media = browser.matchMedia('(prefers-color-scheme: dark)');
  let preference: ThemePreference = 'system';
  try { preference = validPreference(browser.localStorage.getItem(THEME_KEY)); } catch {}
  const resolve = (): Theme => preference === 'system' ? media.matches ? 'dark' : 'light' : preference;
  let snapshot = { preference, theme: resolve() };
  const listeners = new Set<() => void>();
  function update() {
    const theme = resolve();
    browser.document.documentElement.dataset.theme = theme;
    browser.document.documentElement.style.colorScheme = theme;
    if (snapshot.preference === preference && snapshot.theme === theme) return;
    snapshot = { preference, theme };
    listeners.forEach(listener => listener());
  }
  browser.document.documentElement.dataset.theme = snapshot.theme;
  browser.document.documentElement.style.colorScheme = snapshot.theme;
  const onSystem = () => update();
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THEME_KEY && event.key !== null) return;
    if (event.storageArea && event.storageArea !== browser.localStorage) return;
    preference = validPreference(event.newValue);
    update();
  };
  media.addEventListener('change', onSystem);
  browser.addEventListener('storage', onStorage);
  function setPreference(value: ThemePreference) {
    preference = validPreference(value);
    try { browser.localStorage.setItem(THEME_KEY, preference); } catch {}
    update();
  }
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    setPreference,
    toggleTheme: () => setPreference(resolve() === 'dark' ? 'light' : 'dark'),
    dispose() { media.removeEventListener('change', onSystem); browser.removeEventListener('storage', onStorage); listeners.clear(); },
  };
}
let store: ReturnType<typeof createThemeStore> | undefined;
function getStore() { return store ??= createThemeStore(window); }
const serverSnapshot = { preference: 'system' as const, theme: 'light' as const };
const serverStore = { getSnapshot: () => serverSnapshot, subscribe: (_listener: () => void) => () => {}, setPreference: (_value: ThemePreference) => {}, toggleTheme: () => {} };
export function useTheme() {
  const shared = typeof window === 'undefined' ? serverStore : getStore();
  const snapshot = useSyncExternalStore(shared.subscribe, shared.getSnapshot, () => serverSnapshot);
  return { ...snapshot, setPreference: shared.setPreference, toggleTheme: shared.toggleTheme };
}
